import * as monacoEditor from 'monaco-editor';
import React, { useCallback } from 'react';

import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';

import { isGraphqlFieldPath } from '../helpers/graphqlHelpers';
import { GraphQLPath } from '../helpers/operationPathHelpers';
import { useDerivedEditorValue } from '../hooks/useDerivedEditorValue';
import { useEditorModel } from '../hooks/useEditorModel';

import { getCurrentDocsContext } from './getFieldToggle/getCurrentDocsContext';
import { getFieldToggle } from './getFieldToggle/getFieldToggle';
import { ToggleEditorContext } from './shared';

export function useFieldToggle(
  path: GraphQLPath,
  toggleEditorContext: ToggleEditorContext,
) {
  const {
    editor,
    variablesEditor,
    schema,
    setActiveEditorBottomTab,
    shouldManageVariables,
  } = toggleEditorContext;
  const trackCustomEvent = useTrackCustomEvent();
  const model = useEditorModel(editor);
  const variablesEditorPosition = useDerivedEditorValue({
    editor: variablesEditor,
    getValue: useCallback(
      (_, position: monacoEditor.Position | undefined) => position,
      [],
    ),
    withPosition: true,
  });
  const currentDocsContext = useDerivedEditorValue({
    editor,
    getValue: useCallback(
      (_, editorPosition: monacoEditor.Position | undefined) =>
        model &&
        isGraphqlFieldPath(path) &&
        getCurrentDocsContext(
          model,
          editorPosition ?? new monacoEditor.Position(1, 1),
          path,
          schema,
        ),
      [path, model, schema],
    ),
    withPosition: true,
  });
  return React.useMemo(
    () =>
      currentDocsContext
        ? getFieldToggle(
            currentDocsContext,
            path,
            schema,
            editor,
            variablesEditor,
            variablesEditorPosition,
            setActiveEditorBottomTab,
            shouldManageVariables,
            trackCustomEvent,
          )
        : undefined,
    [
      currentDocsContext,
      path,
      schema,
      editor,
      variablesEditor,
      variablesEditorPosition,
      setActiveEditorBottomTab,
      shouldManageVariables,
      trackCustomEvent,
    ],
  );
}
